import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Settings } from 'lucide-react';
import { useContactModal } from '../contexts/ContactModalContext';
import { supabase } from '../lib/supabase';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const { openModal } = useContactModal();



  useEffect(() => {
    // Check auth status
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };

    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add effect to handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Hide navbar on admin routes
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/projects', label: 'Projects' },
    { path: '/learning-center', label: 'Learning Center' },
    // Conditionally add admin link
    ...(isAuthenticated ? [{ path: '/admin', label: 'Admin', icon: <Settings className="w-4 h-4" /> }] : []),
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 transition-all duration-300">
      <div className="bg-gray-900 text-white text-center py-2 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 px-4 text-sm relative z-50 shadow-sm">
        <div className="font-medium tracking-wide hidden sm:block">Premium Custom Home Builders in Kannur, Kerala</div>
        <div className="font-medium tracking-wide sm:hidden">Premium Builders in Kannur</div>
        <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-xs sm:text-sm">
          <span className="text-yellow-400 font-bold tracking-widest text-base sm:text-lg leading-none">★★★★★</span>
          <span className="font-semibold">4.9/5</span>
          <span className="text-gray-300 hidden md:inline">based on 40+ Google Reviews</span>
        </div>
      </div>
      <nav
        className={`w-full transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-4' : 'bg-transparent py-4 md:py-6'
        }`}
      >
      <div className="container-padding">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="./logo.png" 
              alt="Master Home Logo" 
              className={`h-10 w-auto transition-all duration-200 ${
                isScrolled ? '' : 'brightness-0 invert'
              }`}
            />
            <div className={`transition-all duration-200 ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}>
              <h1 className="text-2xl font-bold font-display">Master Home</h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(({ path, label, icon }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-2 font-medium transition-colors ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-primary-600' 
                    : 'text-white hover:text-white/80'
                } ${location.pathname === path ? 'text-primary-600' : ''}`}
              >
                {icon}
                {label}
              </Link>
            ))}
            <button
              onClick={openModal}
              className="btn-primary"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-600"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white absolute w-full left-0 shadow-2xl border-t border-gray-100 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="container-padding py-6">
            <div className="flex flex-col space-y-4">
              {navLinks.map(({ path, label, icon }) => (
                <Link
                  key={path}
                  to={path}
                  className="flex items-center gap-2 font-medium text-gray-700 hover:text-primary-600"
                  onClick={() => setIsOpen(false)}
                >
                  {icon}
                  {label}
                </Link>
              ))}
              <button
                onClick={() => {
                  setIsOpen(false);
                  openModal();
                }}
                className="btn-primary w-full"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  </header>
  );
};

export default Navbar;
